import { type ReactNode, useState } from "react";
import { Row, SubtleButton } from "../ConnectorDropdown";
import { Dropdown } from "../Dropdown";
import { Icon, type IconName } from "../Icon";
import { Text } from "../Text";

type MenuItem = {
  left?: ReactNode;
  label: string;
  onClick: () => void;
};

// A row inside a manage-popover (ConnectorDropdown): icon, title, optional
// subtitle, and an overflow menu revealed on hover. The kebab stays mounted
// (opacity, not display) while its menu is open so the menu's anchor doesn't
// vanish when the mouse leaves the row.
export function PopoverManageRow({
  icon,
  title,
  subtitle,
  dimmed,
  menuItems,
  onClick,
}: {
  icon: IconName;
  title: string;
  subtitle?: string;
  dimmed?: boolean;
  menuItems: MenuItem[];
  onClick?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const show = hovered || menuOpen;
  return (
    <Row
      style={{ cursor: onClick ? "pointer" : "default" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <Icon name={icon} size={16} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          ellipsis
          variant="text-regular"
          color={dimmed ? "var(--text-secondary)" : "var(--text-primary)"}
        >
          {title}
        </Text>
        {subtitle && (
          <Text ellipsis variant="text-small" color="var(--text-secondary)">
            {subtitle}
          </Text>
        )}
      </div>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          flexShrink: 0,
          marginLeft: "auto",
          marginRight: -4,
          opacity: show ? 1 : 0,
          pointerEvents: show ? undefined : "none",
        }}
      >
        <Dropdown
          align="bottom"
          minWidth={160}
          onOpenChange={setMenuOpen}
          opener={<SubtleButton variant="secondary" icon="more" />}
          sections={[
            {
              items: menuItems.map((m) => ({
                type: "button" as const,
                left: m.left,
                label: m.label,
                onClick: m.onClick,
              })),
            },
          ]}
        />
      </div>
    </Row>
  );
}
